/**
 * Confirmation guard for the destructive forms of the administration.
 */

import { askConfirmation } from "./modal";
import { allowUnload } from "./unsaved";
import { queryAll } from "../core/dom";
import { text } from "../core/i18n";

/**
 * Ask the visitor for a confirmation before submitting a form.
 *
 * The dialog answers later than a submit event can wait, so the submission is
 * always stopped and started again from the answer. `submit()` rather than
 * `requestSubmit()`: the second one raises the event again and would ask the
 * same question for ever.
 */
export function guardForm(form: HTMLFormElement): void {
    form.addEventListener("submit", (event: Event) => {
        if (form.dataset["confirmed"] === "yes") {
            return;
        }

        event.preventDefault();

        const question = form.dataset["confirm"] ?? text("js.confirm.default", "Naozaj chcete pokračovať?");

        void askConfirmation(question, {
            confirm: form.dataset["confirmLabel"] ?? text("js.confirm.confirm", "Potvrdiť"),
            danger: true,
        }).then((agreed) => {
            if (!agreed) {
                return;
            }

            form.dataset["confirmed"] = "yes";
            // The form is leaving with its changes, so the unsaved guard has
            // nothing left to warn about.
            allowUnload();
            form.submit();
        });
    });
}

/** Wire every form carrying a `data-confirm` attribute. */
export function initConfirmations(): void {
    for (const form of queryAll<HTMLFormElement>("form[data-confirm]")) {
        guardForm(form);
    }
}

/**
 * Open the collapsible holding a field the browser refuses.
 *
 * The wording of a form is split into one collapsible per language. A field
 * inside a closed one cannot be focused, so the browser would block the
 * submit without telling the editor where the problem is.
 */
export function revealInvalidField(field: Element): void {
    for (let block = field.closest("details"); block !== null; block = block.parentElement?.closest("details") ?? null) {
        block.open = true;
    }
}

/** Wire the reveal on every field of the validated forms. */
export function initInvalidReveal(): void {
    for (const form of queryAll<HTMLFormElement>("form[data-validate]")) {
        for (const field of queryAll<HTMLElement>("input, select, textarea", form)) {
            field.addEventListener("invalid", () => revealInvalidField(field));
        }
    }
}

/** Prevent a double submit of the validated forms. */
export function initSubmitGuards(): void {
    for (const form of queryAll<HTMLFormElement>("form[data-validate]")) {
        form.addEventListener("submit", () => {
            if (!form.checkValidity()) {
                return;
            }

            for (const button of queryAll<HTMLButtonElement>("button[type=submit]", form)) {
                if (button.form !== form) {
                    continue;
                }

                window.setTimeout(() => {
                    button.disabled = true;
                    button.classList.add("is-disabled");
                }, 0);
            }
        });
    }
}
